import * as React from "react";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import Toolbar from "@mui/material/Toolbar";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Fade from "@mui/material/Fade";
import Collapse from "@mui/material/Collapse";
import { useNavigate } from "react-router-dom";
import projectsData from "../data/projectsData";

const navItems = [
  { label: "home", path: "/" },
  { label: "about", path: "/about" },
  { label: "work", path: "/work" },
];

function DrawerAppBar(props) {
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [workDropdownOpen, setWorkDropdownOpen] = React.useState(false);
  const [mobileWorkExpanded, setMobileWorkExpanded] = React.useState(false);
  const dropdownRef = React.useRef(null);
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
    setMobileWorkExpanded(false);
  };

  // Close desktop dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setWorkDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const container =
    window !== undefined ? () => window().document.body : undefined;

  const navigateToProject = (header) => {
    const projectId = header.toLowerCase().replace(/\s+/g, "-");
    navigate(`/work#${projectId}`);
    setWorkDropdownOpen(false);
    setMobileOpen(false);
    setMobileWorkExpanded(false);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <AppBar
        component="nav"
        elevation={0}
        sx={{
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #E0E0E0",
          color: "#333333",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", maxWidth: "1000px", width: "100%", margin: "0 auto", px: { xs: "1rem", sm: "1rem", md: "0rem" } }}>
          {/* Left: Brand Text */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => navigate("/")}
          >
            <Box
              sx={{
                fontFamily: "Avenir",
                fontSize: "0.85rem",
                letterSpacing: "0.2rem",
                fontWeight: "600",
                color: "#333333",
                textAlign: "left",
              }}
            >
              ALCHEMY COLLECTIVE
            </Box>
          </Box>

          {/* Right: Desktop Nav OR Mobile Hamburger */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center" }}>
              {navItems.map((item) =>
                item.label === "work" ? (
                  <Box
                    key={item.label}
                    ref={dropdownRef}
                    sx={{ position: "relative" }}
                    onMouseEnter={() => setWorkDropdownOpen(true)}
                    onMouseLeave={() => setWorkDropdownOpen(false)}
                  >
                    <Button
                      sx={{
                        color: "#333333",
                        fontFamily: "Avenir",
                        fontWeight: "500",
                        textTransform: "none",
                        mx: 1,
                        "&:hover": {
                          backgroundColor: "transparent",
                          textDecoration: "underline",
                          textUnderlineOffset: "4px",
                        },
                      }}
                      onClick={() => navigate(item.path)}
                      endIcon={
                        workDropdownOpen ? (
                          <ExpandLessIcon sx={{ fontSize: "1rem !important" }} />
                        ) : (
                          <ExpandMoreIcon sx={{ fontSize: "1rem !important" }} />
                        )
                      }
                    >
                      {item.label}
                    </Button>

                    {/* Desktop Dropdown */}
                    {workDropdownOpen && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          backgroundColor: "#fff",
                          border: "1px solid #eee",
                          borderRadius: "8px",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                          minWidth: "200px",
                          py: 1,
                          zIndex: 1300,
                        }}
                      >
                        {projectsData.map((proj, idx) => (
                          <Box
                            key={idx}
                            onClick={() => navigateToProject(proj.header)}
                            sx={{
                              px: 2,
                              py: 1,
                              cursor: "pointer",
                              fontFamily: "Avenir",
                              fontSize: "0.85rem",
                              color: "#333",
                              transition: "all 0.15s ease",
                              "&:hover": {
                                backgroundColor: "#f5f5f5",
                                pl: 2.5,
                              },
                            }}
                          >
                            {proj.header}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Button
                    key={item.label}
                    sx={{
                      color: "#333333",
                      fontFamily: "Avenir",
                      fontWeight: "500",
                      textTransform: "none",
                      mx: 1,
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                        textUnderlineOffset: "4px",
                      },
                    }}
                    onClick={() => navigate(item.path)}
                  >
                    {item.label}
                  </Button>
                )
              )}
            </Box>

            {/* Mobile Hamburger */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerToggle}
              sx={{ ml: 1, display: { sm: "none" } }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Modal Menu */}
      <Dialog
        open={mobileOpen}
        onClose={handleDrawerToggle}
        TransitionComponent={Fade}
        container={container}
        PaperProps={{
          sx: {
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            borderRadius: "16px",
            boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.1)",
            width: "80%",
            maxWidth: "300px",
            maxHeight: "80vh",
            m: 2,
            overflow: "auto",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(4px)",
              backgroundColor: "rgba(0, 0, 0, 0.2)",
            },
          },
        }}
      >
        <DialogContent sx={{ p: 4, textAlign: "center", position: "relative" }}>
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#333333",
            }}
          >
            <CloseIcon />
          </IconButton>

          <List sx={{ mt: 2 }}>
            {navItems.map((item) =>
              item.label === "work" ? (
                <React.Fragment key={item.label}>
                  <ListItem disablePadding sx={{ justifyContent: "center", mb: 0.5 }}>
                    <ListItemButton
                      sx={{ textAlign: "center", justifyContent: "center", borderRadius: "8px" }}
                      onClick={() => setMobileWorkExpanded((prev) => !prev)}
                    >
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontFamily: "Audrey",
                          fontSize: "1.5rem",
                          color: "#333333",
                          letterSpacing: "1px",
                        }}
                      />
                      {mobileWorkExpanded ? (
                        <ExpandLessIcon sx={{ color: "#333" }} />
                      ) : (
                        <ExpandMoreIcon sx={{ color: "#333" }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={mobileWorkExpanded} timeout="auto">
                    <List disablePadding sx={{ pl: 1 }}>
                      {/* "All Projects" link */}
                      <ListItem disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          sx={{ textAlign: "center", justifyContent: "center", borderRadius: "8px", py: 0.5 }}
                          onClick={() => {
                            navigate("/work");
                            handleDrawerToggle();
                          }}
                        >
                          <ListItemText
                            primary="All Projects"
                            primaryTypographyProps={{
                              fontFamily: "Avenir",
                              fontSize: "0.9rem",
                              color: "#555",
                              fontWeight: 600,
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                      {projectsData.map((proj, idx) => (
                        <ListItem key={idx} disablePadding sx={{ mb: 0.25 }}>
                          <ListItemButton
                            sx={{
                              textAlign: "center",
                              justifyContent: "center",
                              borderRadius: "8px",
                              py: 0.5,
                            }}
                            onClick={() => navigateToProject(proj.header)}
                          >
                            <ListItemText
                              primary={proj.header}
                              primaryTypographyProps={{
                                fontFamily: "Avenir",
                                fontSize: "0.85rem",
                                color: "#555",
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              ) : (
                <ListItem key={item.label} disablePadding sx={{ justifyContent: "center", mb: 1 }}>
                  <ListItemButton
                    sx={{ textAlign: "center", justifyContent: "center", borderRadius: "8px" }}
                    onClick={() => {
                      navigate(item.path);
                      handleDrawerToggle();
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontFamily: "Audrey",
                        fontSize: "1.5rem",
                        color: "#333333",
                        letterSpacing: "1px",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            )}
          </List>
        </DialogContent>
      </Dialog>

      <Box component="main" sx={{ p: 3 }}>
        <Toolbar />
      </Box>
    </Box>
  );
}

DrawerAppBar.propTypes = {
  window: PropTypes.func,
};

export default DrawerAppBar;
